import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import rsswCreateAccount from "@store/Handlers/Account/RsswCreateAccount";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import React, { ChangeEvent, ReactNode, RefObject, SyntheticEvent, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    rsswCreateAccount,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    sectionGridContainer?: GridContainerProps;
    userGridContainer?: GridContainerProps;
    halfWidthGridItem?: GridItemProps;
    fullWidthGridItem?: GridItemProps;
    spacerHalfWidth?: GridItemProps;
    spacerFullWidth?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitSuccessTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    textStyles?: TypographyProps;
    radioGroupStyles?: RadioGroupProps;
    radioButtonStyles?: RadioProps;
    checkboxGroupStyles?: CheckboxGroupProps;
    checkboxStyles?: CheckboxProps;
}

export const createAccountStyles: CreateAccountStyles = {
    sectionGridContainer: {
        css: css`
            margin-top: 60px;
        `,
    },
    halfWidthGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    fullWidthGridItem: {
        width: 12,
    },
    spacerHalfWidth: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    spacerFullWidth: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    submitErrorTitle: {
        weight: TYPOGRAPHY.weight.bold,
        size: TYPOGRAPHY.size.base,
        color: COLORS.error,
        css: css`
            border: 1px solid ${COLORS.error};
            background-color: ${COLORS.errorBackground};
            padding: 20px;
        `,
    },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    submitSuccessTitle: {
        weight: TYPOGRAPHY.weight.bold,
        size: TYPOGRAPHY.size.base,
        color: COLORS.success,
        css: css`
            border: 1px solid ${COLORS.success};
            background: ${COLORS.successBackground};
            padding: 20px;
        `,
    },
    textStyles: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.small,
    },
    radioGroupStyles: {
        labelProps: {
            color: COLORS.text.dark,
            size: TYPOGRAPHY.size.small,
            css: css`
                font-weight: ${TYPOGRAPHY.weight.medium};
            `,
        },
    },
    radioButtonStyles: {
        typographyProps: {
            color: COLORS.text.dark,
        },
    },
    checkboxGroupStyles: {
        labelProps: {
            color: COLORS.text.dark,
            size: TYPOGRAPHY.size.small,
            weight: TYPOGRAPHY.weight.medium,
        },
        errorProps: {
            weight: TYPOGRAPHY.weight.base,
            size: TYPOGRAPHY.size.smaller,
        },
    },
    checkboxStyles: {
        typographyProps: {
            color: COLORS.text.dark,
        },
    },
};

const styles = createAccountStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const phoneRegexp = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;
const StyledForm = getStyledWrapper("form");

const RsswCreateAccount = ({ rsswCreateAccount, validateReCaptcha }: Props) => {
    const getRequiredErrorMessage = (inputField: string) => siteMessage("Field_Required", inputField).toString();

    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail").toString();
    const phoneFieldMessage = siteMessage("CreateNewAccountInfo_Phone_ValidPhone").toString();
    const faxFieldMessage = siteMessage("CreateNewAccountInfo_Fax_ValidFax").toString();

    const translateCompany = translate("Company");
    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState(getRequiredErrorMessage(translateCompany));

    const translateAddress = translate("Address");
    const [address, setAddress] = useState("");
    const [addressError, setAddressError] = useState(getRequiredErrorMessage(translateAddress));

    const translatePostal = translate("Postal Code");
    const [postal, setPostal] = useState("");
    const [postalError, setPostalError] = useState(getRequiredErrorMessage(translatePostal));

    const translateCity = translate("City");
    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState(getRequiredErrorMessage(translateCity));

    const translateProvince = translate("Province");
    const [province, setProvince] = useState("");
    const [provinceError, setProvinceError] = useState(getRequiredErrorMessage(translateProvince));

    const translatePhone = translate("Phone");
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState(getRequiredErrorMessage(translatePhone));

    const translateFax = translate("Fax");
    const [fax, setFax] = useState("");
    const [faxError, setFaxError] = useState("");

    const [deliveryAddressRadio, setDeliveryAddressRadio] = useState("false");
    const deliveryAddressRadioCondition = deliveryAddressRadio === "true";

    const [shipAddress, setShipAddress] = useState("");
    const [shipPostal, setShipPostal] = useState("");
    const [shipCity, setShipCity] = useState("");
    const [shipProvince, setShipProvince] = useState("");

    const [shipPhone, setShipPhone] = useState("");
    const [shipPhoneError, setShipPhoneError] = useState("");
    const [shipFax, setShipFax] = useState("");
    const [shipFaxError, setShipFaxError] = useState("");

    const [companysOwnerRadio, setCompanysOwnerRadio] = useState("true");
    const companysOwnerRadioCondition = companysOwnerRadio === "false";

    const translateOwnersName = translate("Owner's name");
    const [ownersName, setOwnersName] = useState("");
    const [ownersNameError, setOwnersNameError] = useState("");

    const translateFirstName = translate("First Name");
    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState(getRequiredErrorMessage(translateFirstName));

    const translateLastName = translate("Last Name");
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState(getRequiredErrorMessage(translateLastName));

    const translateEmail = translate("Email Address");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(getRequiredErrorMessage(translateEmail));

    const [personsTitle, setPersonsTitle] = useState("");
    const [personsTitleId, setPersonsTitleId] = useState(0);
    const personsTitleIdCondition = personsTitleId === 4;

    const translateMakeChoice = translate("Make a choice");
    const translateOwner = translate("Owner");
    const translateBuyer = translate("Buyer");
    const translateResponsibleAccount = translate("Responsible account payable");
    const translateOther = translate("Other");

    const translateOtherTitle = translate("Other Title");
    const [otherTitle, setOtherTitle] = useState("");
    const [otherTitleError, setOtherTitleError] = useState("");

    const [extraPerson, setExtraPerson] = useState(false);
    const extraPersonCondition = extraPerson === true;

    const [extraFirstName, setExtraFirstName] = useState("");
    const [extraFirstNameError, setExtraFirstNameError] = useState("");

    const [extraLastName, setExtraLastName] = useState("");
    const [extraLastNameError, setExtraLastNameError] = useState("");

    const [extraEmail, setExtraEmail] = useState("");
    const [extraEmailError, setExtraEmailError] = useState("");

    const [extraPersonsTitle, setExtraPersonsTitle] = useState("");
    const [extraPersonsTitleId, setExtraPersonsTitleId] = useState(0);
    const extraPersonsTitleIdCondition = extraPersonsTitleId === 3;

    const [extraOtherTitle, setExtraOtherTitle] = useState("");
    const [extraOtherTitleError, setExtraOtherTitleError] = useState("");

    const translateOperationSince = translate("In operation since:");
    const [operationSince, setOperationSince] = useState("");

    const [tailgateDelivery, setTailgateDelivery] = useState(false);

    const translateActivityField = translate("Activity field");
    const [activityField, setActivityField] = useState("");
    const [activityFieldError, setActivityFieldError] = useState(getRequiredErrorMessage(translateActivityField));
    const [activityFieldId, setActivityFieldId] = useState(0);

    const translateTire = translate("Tire / Mechanical Workshop");
    const translateReseller = translate("Reseller");
    const translateDealer = translate("Dealer");
    const translateSpecifyDealer = translate("Brand");
    const translateDoors = translate("Number of doors");

    const translateSpecify = translate("Specify");
    const [translateActivityFieldSpecifyBox, setTranslateActivityFieldSpecifyBox] = useState(translateSpecify);
    const [activityFieldSpecifyBox, setActivityFieldSpecifyBox] = useState("");
    const [activityFieldSpecifyBoxType, setActivityFieldSpecifyBoxType] = useState("text");
    const [activityFieldSpecifyBoxHint, setActivityFieldSpecifyBoxHint] = useState("");
    const [activityFieldSpecifyBoxError, setActivityFieldSpecifyBoxError] = useState("");

    const [poNumberRadio, setPoNumberRadio] = useState("true");

    const [purchasesPlanned, setPurchasesPlanned] = useState("");

    const [comments, setComments] = useState("");

    const [legalAgreement, setLegalAgreement] = useState(false);
    const [legalAgreementError, setLegalAgreementError] = useState(
        getRequiredErrorMessage(translate("Legal agreement")),
    );

    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const validateSubmitEnabled = () => {
        return (
            !companyError &&
            !addressError &&
            !postalError &&
            !cityError &&
            !provinceError &&
            !phoneError &&
            !faxError &&
            !shipPhoneError &&
            !shipFaxError &&
            !ownersNameError &&
            !firstNameError &&
            !lastNameError &&
            !emailError &&
            !otherTitleError &&
            !extraFirstNameError &&
            !extraLastNameError &&
            !extraEmailError &&
            !extraOtherTitleError &&
            !activityFieldError &&
            !legalAgreementError
        );
    };

    const textFieldChangeHandler = (
        event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
        setFunction: Function,
        setErrorFunction?: Function,
        errorFieldName?: string,
        conditionalFieldBool = false,
        conditionalField?: any,
    ) => {
        const value = event.currentTarget.value;

        if ((conditionalFieldBool && conditionalField) || !conditionalFieldBool) {
            if (setErrorFunction && errorFieldName) {
                setErrorFunction(value.trim().length <= 0 ? getRequiredErrorMessage(errorFieldName) : "");
            }

            setFunction(value);
        }
    };

    const phoneFieldChangeHandler = (
        event: ChangeEvent<HTMLInputElement>,
        fieldName: string,
        setFunction: Function,
        setErrorFunction: Function,
        message: string,
    ) => {
        const value = event.currentTarget.value;
        const trimmedValueLength = value.trim().length;

        if (event.currentTarget.required) {
            setErrorFunction(
                trimmedValueLength <= 0 ? getRequiredErrorMessage(fieldName) : phoneRegexp.test(value) ? "" : message,
            );
        } else {
            setErrorFunction(
                trimmedValueLength === 0 || (trimmedValueLength > 0 && phoneRegexp.test(value)) ? "" : message,
            );
        }

        setFunction(value);
    };

    const emailFieldChangeHandler = (
        event: ChangeEvent<HTMLInputElement>,
        fieldName: string,
        setFunction: Function,
        setErrorFunction: Function,
        message: string,
    ) => {
        const value = event.currentTarget.value;
        const trimmedValueLength = value.trim().length;

        if (event.currentTarget.required) {
            setErrorFunction(
                trimmedValueLength <= 0 ? getRequiredErrorMessage(fieldName) : emailRegexp.test(value) ? "" : message,
            );
        } else {
            setErrorFunction(
                trimmedValueLength === 0 || (trimmedValueLength > 0 && emailRegexp.test(value)) ? "" : message,
            );
        }

        setFunction(value);
    };

    const onChangeConditionField = (
        specificCondition: number | boolean,
        setFunction: Function,
        setErrorFunction: Function,
        associatedField: string,
        associatedFieldName: string,
    ) => {
        if (specificCondition && associatedField === "") {
            setErrorFunction(getRequiredErrorMessage(associatedFieldName));
        } else if (specificCondition && associatedField !== "") {
            setErrorFunction("");
            setFunction(associatedField);
        } else {
            setErrorFunction("");
            setFunction("");
        }
    };

    const commentsChangeHandler = (comments: string) => {
        setComments(comments);
    };

    const deliveryAddressRadioChangeHandler = (deliveryAddressRadio: ChangeEvent<HTMLInputElement>) => {
        setDeliveryAddressRadio(deliveryAddressRadio.currentTarget.value);

        if (deliveryAddressRadio.currentTarget.value === "false") {
            setShipAddress("");
            setShipPostal("");
            setShipCity("");
            setShipProvince("");
            setShipPhone("");
            setShipFax("");
        }
    };

    const companysOwnerRadioChangeHandler = (companysOwnerRadio: ChangeEvent<HTMLInputElement>) => {
        const value = companysOwnerRadio.currentTarget.value;

        onChangeConditionField(value === "false", setOwnersName, setOwnersNameError, ownersName, translateOwnersName);
        setCompanysOwnerRadio(value);
    };

    const personsTitleChangeHandler = (personsTitle: ChangeEvent<HTMLSelectElement>) => {
        const selectedPersonId = personsTitle.currentTarget.selectedIndex;

        onChangeConditionField(
            selectedPersonId === 4,
            setOtherTitle,
            setOtherTitleError,
            otherTitle,
            translateOtherTitle,
        );
        setPersonsTitle(personsTitle.currentTarget.value);
        setPersonsTitleId(selectedPersonId);
    };

    const extraPersonChangeHandler = (_: SyntheticEvent<Element, Event>, value: boolean) => {
        onChangeExtraPerson(value);
        setExtraPerson(value);
    };

    const onChangeExtraPerson = (extraPerson: boolean) => {
        if (extraPerson) {
            setExtraFirstNameError(getRequiredErrorMessage(translateFirstName));
            setExtraLastNameError(getRequiredErrorMessage(translateLastName));
            setExtraEmailError(getRequiredErrorMessage(translateEmail));
        } else {
            setExtraFirstNameError("");
            setExtraLastNameError("");
            setExtraEmailError("");
            setExtraFirstName("");
            setExtraLastName("");
            setExtraEmail("");
            setExtraPersonsTitleId(0);
        }

        onChangeConditionField(
            extraPerson && extraPersonsTitleIdCondition,
            setExtraOtherTitle,
            setExtraOtherTitleError,
            extraOtherTitle,
            translateOtherTitle,
        );
    };

    const extraPersonsTitleChangeHandler = (extraPersonsTitle: ChangeEvent<HTMLSelectElement>) => {
        const selectedPersonId = extraPersonsTitle.currentTarget.selectedIndex;

        onChangeConditionField(
            selectedPersonId === 3,
            setExtraOtherTitle,
            setExtraOtherTitleError,
            extraOtherTitle,
            translateOtherTitle,
        );
        setExtraPersonsTitle(extraPersonsTitle.currentTarget.value);
        setExtraPersonsTitleId(selectedPersonId);
    };

    const activityFieldChangeHandler = (activityField: ChangeEvent<HTMLSelectElement>) => {
        const selectedFieldId = activityField.currentTarget.selectedIndex;
        const hintText = translate("Please enter a number less than or equal to 9999.");
        setActivityFieldSpecifyBox("");

        if (selectedFieldId === 0) {
            setActivityFieldError(getRequiredErrorMessage(translateActivityField));
        } else {
            setActivityFieldError("");
        }

        if (selectedFieldId === 1) {
            setTranslateActivityFieldSpecifyBox(translate(translateDoors));
            setActivityFieldSpecifyBoxType("number");
            setActivityFieldSpecifyBoxHint(hintText);
            setActivityFieldSpecifyBoxError(getRequiredErrorMessage(translate(translateDoors)));
        } else if (selectedFieldId === 3) {
            setTranslateActivityFieldSpecifyBox(translateSpecifyDealer);
            setActivityFieldSpecifyBoxType("text");
            setActivityFieldSpecifyBoxHint("");
            setActivityFieldSpecifyBoxError(getRequiredErrorMessage(translateSpecifyDealer));
        } else if (selectedFieldId === 4) {
            setTranslateActivityFieldSpecifyBox(translateSpecify);
            setActivityFieldSpecifyBoxType("text");
            setActivityFieldSpecifyBoxHint("");
            setActivityFieldSpecifyBoxError(getRequiredErrorMessage(translateSpecify));
        } else {
            setActivityFieldSpecifyBoxType("none");
            setActivityFieldSpecifyBoxHint("");
            setActivityFieldSpecifyBoxError("");
        }

        setActivityField(activityField.currentTarget.value);
        setActivityFieldId(selectedFieldId);
    };

    const activityFieldBoxChangeHandler = (event: ChangeEvent<HTMLInputElement>, conditionalField?: any) => {
        const value = event.currentTarget.value;

        if (activityFieldId !== 0 && (activityFieldSpecifyBoxType === "number" ? value.length <= 4 : true)) {
            if (activityFieldId === 4) {
                setActivityFieldSpecifyBoxError(
                    value.trim().length <= 0 ? getRequiredErrorMessage(translateSpecify) : "",
                );
            } else {
                setActivityFieldSpecifyBoxError("");
            }

            setActivityFieldSpecifyBox(value);
        }
    };

    const tailgateDeliveryChangeHandler = (_: SyntheticEvent<Element, Event>, value: boolean) => {
        setTailgateDelivery(value);
    };

    const legalAgreementChangeHandler = (_: SyntheticEvent<Element, Event>, value: boolean) => {
        setLegalAgreementError(!value ? getRequiredErrorMessage(translate("Legal agreement")) : "");
        setLegalAgreement(value);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            onChangeConditionField(
                companysOwnerRadioCondition,
                setOwnersName,
                setOwnersNameError,
                ownersName,
                translateOwnersName,
            );
            onChangeConditionField(
                personsTitleIdCondition,
                setOtherTitle,
                setOtherTitleError,
                otherTitle,
                translateOtherTitle,
            );
            onChangeExtraPerson(extraPerson);
            onChangeConditionField(
                extraPersonsTitleIdCondition,
                setExtraOtherTitle,
                setExtraOtherTitleError,
                extraOtherTitle,
                translateOtherTitle,
            );

            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        rsswCreateAccount({
            company,
            address,
            postal,
            city,
            province,
            phone,
            fax,
            deliveryAddressRadio,
            shipAddress,
            shipPostal,
            shipCity,
            shipProvince,
            shipPhone,
            shipFax,
            companysOwnerRadio,
            ownersName,
            firstName,
            lastName,
            email,
            personsTitle,
            otherTitle,
            extraPerson,
            extraFirstName,
            extraLastName,
            extraEmail,
            extraPersonsTitle,
            extraOtherTitle,
            operationSince,
            activityField,
            translateActivityFieldSpecifyBox,
            activityFieldSpecifyBox,
            poNumberRadio,
            purchasesPlanned,
            comments,
            legalAgreement,
            tailgateDelivery,
            onSuccess: () => {
                setSuccessMessage(translate("CreateAccountSuccessMessage"));
                setErrorMessage("");
                toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
            },
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <StyledWrapper>
                <Typography as="h2" variant="h2">
                    {translate("Request to open an account")}
                </Typography>
                <Typography {...styles.textStyles} variant="p">
                    {translate("Fill out the following form:")}
                </Typography>
            </StyledWrapper>
            <GridContainer {...styles.sectionGridContainer}>
                <GridItem {...styles.fullWidthGridItem}>
                    <Typography as="h2" variant="h2">
                        {translate("Administrative address")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        name="company"
                        label={translateCompany}
                        value={company}
                        required
                        onChange={e => textFieldChangeHandler(e, setCompany, setCompanyError, translateCompany)}
                        error={isSubmitted && companyError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="address"
                        label={translateAddress}
                        value={address}
                        required
                        onChange={e => textFieldChangeHandler(e, setAddress, setAddressError, translateAddress)}
                        error={isSubmitted && addressError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="postal"
                        label={translatePostal}
                        value={postal}
                        required
                        onChange={e => textFieldChangeHandler(e, setPostal, setPostalError, translatePostal)}
                        error={isSubmitted && postalError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="city"
                        label={translateCity}
                        value={city}
                        required
                        onChange={e => textFieldChangeHandler(e, setCity, setCityError, translateCity)}
                        error={isSubmitted && cityError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <Select
                        label={translateProvince}
                        required
                        onChange={e => textFieldChangeHandler(e, setProvince, setProvinceError, translateProvince)}
                        error={isSubmitted && provinceError}
                    >
                        <option value="">{translate("Choose a Province")}</option>
                        <option value={translate("Alberta")}>{translate("Alberta")}</option>
                        <option value={translate("ColombieBritannique")}>{translate("ColombieBritannique")}</option>
                        <option value={translate("PrinceEdouard")}>{translate("PrinceEdouard")}</option>
                        <option value={translate("Manitoba")}>{translate("Manitoba")}</option>
                        <option value={translate("NewBrunswick")}>{translate("NewBrunswick")}</option>
                        <option value={translate("NouvelleEcosse")}>{translate("NouvelleEcosse")}</option>
                        <option value={translate("Nunavut")}>{translate("Nunavut")}</option>
                        <option value={translate("Ontario")}>{translate("Ontario")}</option>
                        <option value={translate("Quebec")}>{translate("Quebec")}</option>
                        <option value={translate("Saskatchewan")}>{translate("Saskatchewan")}</option>
                        <option value={translate("TerreNeuve")}>{translate("TerreNeuve")}</option>
                        <option value={translate("Territoires")}>{translate("Territoires")}</option>
                        <option value={translate("Yukon")}>{translate("Yukon")}</option>
                    </Select>
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="phone"
                        label={translatePhone}
                        value={phone}
                        required
                        onChange={e =>
                            phoneFieldChangeHandler(e, translatePhone, setPhone, setPhoneError, phoneFieldMessage)
                        }
                        error={isSubmitted && phoneError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="fax"
                        label={translateFax}
                        value={fax}
                        onChange={e => phoneFieldChangeHandler(e, translateFax, setFax, setFaxError, faxFieldMessage)}
                        error={isSubmitted && faxError}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <RadioGroup
                        name="deliveryAddressRadio"
                        label={translate("Is the delivery address different from the postal address?")}
                        value={deliveryAddressRadio}
                        required
                        onChangeHandler={deliveryAddressRadioChangeHandler}
                        {...styles.radioGroupStyles}
                    >
                        <Radio value="true" {...styles.radioButtonStyles}>
                            {translate("Yes")}
                        </Radio>
                        <Radio value="false" {...styles.radioButtonStyles}>
                            {translate("No")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
                {deliveryAddressRadioCondition && (
                    <>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Typography as="h2" variant="h2">
                                {translate("Shipping address")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="shipAddress"
                                label={translateAddress}
                                value={shipAddress}
                                onChange={e =>
                                    textFieldChangeHandler(
                                        e,
                                        setShipAddress,
                                        undefined,
                                        translateAddress,
                                        true,
                                        deliveryAddressRadioCondition,
                                    )
                                }
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="shipPostal"
                                label={translatePostal}
                                value={shipPostal}
                                onChange={e =>
                                    textFieldChangeHandler(
                                        e,
                                        setShipPostal,
                                        undefined,
                                        translatePostal,
                                        true,
                                        deliveryAddressRadioCondition,
                                    )
                                }
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="shipCity"
                                label={translateCity}
                                value={shipCity}
                                onChange={e =>
                                    textFieldChangeHandler(
                                        e,
                                        setShipCity,
                                        undefined,
                                        translateCity,
                                        true,
                                        deliveryAddressRadioCondition,
                                    )
                                }
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <Select
                                label={translateProvince}
                                onChange={e =>
                                    textFieldChangeHandler(
                                        e,
                                        setShipProvince,
                                        undefined,
                                        translateProvince,
                                        true,
                                        deliveryAddressRadioCondition,
                                    )
                                }
                            >
                                <option value="">{translate("Choose a Province")}</option>
                                <option value={translate("Alberta")}>{translate("Alberta")}</option>
                                <option value={translate("ColombieBritannique")}>
                                    {translate("ColombieBritannique")}
                                </option>
                                <option value={translate("PrinceEdouard")}>{translate("PrinceEdouard")}</option>
                                <option value={translate("Manitoba")}>{translate("Manitoba")}</option>
                                <option value={translate("NewBrunswick")}>{translate("NewBrunswick")}</option>
                                <option value={translate("NouvelleEcosse")}>{translate("NouvelleEcosse")}</option>
                                <option value={translate("Nunavut")}>{translate("Nunavut")}</option>
                                <option value={translate("Ontario")}>{translate("Ontario")}</option>
                                <option value={translate("Quebec")}>{translate("Quebec")}</option>
                                <option value={translate("Saskatchewan")}>{translate("Saskatchewan")}</option>
                                <option value={translate("TerreNeuve")}>{translate("TerreNeuve")}</option>
                                <option value={translate("Territoires")}>{translate("Territoires")}</option>
                                <option value={translate("Yukon")}>{translate("Yukon")}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="shipPhone"
                                label={translatePhone}
                                value={shipPhone}
                                onChange={e =>
                                    phoneFieldChangeHandler(
                                        e,
                                        translatePhone,
                                        setShipPhone,
                                        setShipPhoneError,
                                        phoneFieldMessage,
                                    )
                                }
                                error={isSubmitted && shipPhoneError}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="shipFax"
                                label={translateFax}
                                value={shipFax}
                                onChange={e =>
                                    phoneFieldChangeHandler(
                                        e,
                                        translateFax,
                                        setShipFax,
                                        setShipFaxError,
                                        faxFieldMessage,
                                    )
                                }
                                error={isSubmitted && shipFaxError}
                            />
                        </GridItem>
                    </>
                )}
            </GridContainer>
            <GridContainer {...styles.sectionGridContainer}>
                <GridItem {...styles.fullWidthGridItem}>
                    <Typography as="h2" variant="h2">
                        {translate("Additional Information")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <CheckboxGroup {...styles.checkboxGroupStyles} label={translate("Tailgate")}>
                        <Checkbox
                            onChange={tailgateDeliveryChangeHandler}
                            checked={tailgateDelivery}
                            {...styles.checkboxStyles}
                        >
                            {translate("Delivery to tailgate")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <RadioGroup
                        name="companysOwnerRadio"
                        label={translate("Are you the company's owner?")}
                        value={companysOwnerRadio}
                        required
                        onChangeHandler={companysOwnerRadioChangeHandler}
                        {...styles.radioGroupStyles}
                    >
                        <Radio value="true" {...styles.radioButtonStyles}>
                            {translate("Yes")}
                        </Radio>
                        <Radio value="false" {...styles.radioButtonStyles}>
                            {translate("No")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
                {companysOwnerRadioCondition && (
                    <GridItem {...styles.halfWidthGridItem}>
                        <TextField
                            name="ownersName"
                            label={translateOwnersName}
                            value={ownersName}
                            required
                            onChange={e =>
                                textFieldChangeHandler(
                                    e,
                                    setOwnersName,
                                    setOwnersNameError,
                                    translateOwnersName,
                                    true,
                                    companysOwnerRadioCondition,
                                )
                            }
                            error={isSubmitted && ownersNameError}
                        />
                    </GridItem>
                )}
                <GridItem {...styles.fullWidthGridItem}>
                    {companysOwnerRadioCondition && (
                        <Typography as="h2" variant="h2">
                            {translate("What is your name?")}
                        </Typography>
                    )}
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="firstName"
                        label={translateFirstName}
                        value={firstName}
                        required
                        onChange={e => textFieldChangeHandler(e, setFirstName, setFirstNameError, translateFirstName)}
                        error={isSubmitted && firstNameError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="lastName"
                        label={translateLastName}
                        value={lastName}
                        required
                        onChange={e => textFieldChangeHandler(e, setLastName, setLastNameError, translateLastName)}
                        error={isSubmitted && lastNameError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        type="email"
                        label={translateEmail}
                        value={email}
                        required
                        onChange={e =>
                            emailFieldChangeHandler(e, translateEmail, setEmail, setEmailError, emailFieldMessage)
                        }
                        error={isSubmitted && emailError}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <Select label={translate("Title")} onChange={personsTitleChangeHandler}>
                        <option value="">{translateMakeChoice}</option>
                        {!companysOwnerRadioCondition && <option value={translateOwner}>{translateOwner}</option>}
                        <option value={translateBuyer}>{translateBuyer}</option>
                        <option value={translateResponsibleAccount}>{translateResponsibleAccount}</option>
                        <option value={translateOther}>{translateOther}</option>
                    </Select>
                </GridItem>
                <GridItem {...styles.spacerHalfWidth}></GridItem>
                {/* Spacer */}
                {personsTitleIdCondition ? (
                    <GridItem {...styles.halfWidthGridItem}>
                        <TextField
                            name="otherTitle"
                            label={translate("Specify other title:")}
                            value={otherTitle}
                            required
                            onChange={e =>
                                textFieldChangeHandler(
                                    e,
                                    setOtherTitle,
                                    setOtherTitleError,
                                    translateOtherTitle,
                                    true,
                                    personsTitleIdCondition,
                                )
                            }
                            error={isSubmitted && otherTitleError}
                        />
                    </GridItem>
                ) : (
                    <GridItem {...styles.spacerHalfWidth}></GridItem> /* Spacer */
                )}
                <GridItem {...styles.halfWidthGridItem}>
                    <CheckboxGroup
                        {...styles.checkboxGroupStyles}
                        label={translate("Do you want to add another person?")}
                    >
                        <Checkbox onChange={extraPersonChangeHandler} checked={extraPerson} {...styles.checkboxStyles}>
                            {translate("Add an extra person")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                {extraPersonCondition ? (
                    <>
                        <GridItem {...styles.spacerFullWidth}></GridItem>
                        {/* Spacer */}
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="extraFirstName"
                                label={translateFirstName}
                                value={extraFirstName}
                                required
                                onChange={e =>
                                    textFieldChangeHandler(
                                        e,
                                        setExtraFirstName,
                                        setExtraFirstNameError,
                                        translateFirstName,
                                        true,
                                        extraPersonCondition,
                                    )
                                }
                                error={isSubmitted && extraFirstNameError}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                name="extraLastName"
                                label={translateLastName}
                                value={extraLastName}
                                required
                                onChange={e =>
                                    textFieldChangeHandler(
                                        e,
                                        setExtraLastName,
                                        setExtraLastNameError,
                                        translateLastName,
                                        true,
                                        extraPersonCondition,
                                    )
                                }
                                error={isSubmitted && extraLastNameError}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                type="extraEmail"
                                label={translateEmail}
                                value={extraEmail}
                                required
                                onChange={e =>
                                    emailFieldChangeHandler(
                                        e,
                                        translateEmail,
                                        setExtraEmail,
                                        setExtraEmailError,
                                        emailFieldMessage,
                                    )
                                }
                                error={isSubmitted && extraEmailError}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <Select label={translate("Title")} onChange={extraPersonsTitleChangeHandler}>
                                <option value="">{translateMakeChoice}</option>
                                {companysOwnerRadioCondition && (
                                    <option value={translateOwner}>{translateOwner}</option>
                                )}
                                <option value={translateBuyer}>{translateBuyer}</option>
                                <option value={translateResponsibleAccount}>{translateResponsibleAccount}</option>
                                <option value={translateOther}>{translateOther}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}></GridItem>
                        {/* Spacer */}
                        {extraPersonsTitleId === 3 ? (
                            <GridItem {...styles.halfWidthGridItem}>
                                <TextField
                                    name="extraOtherTitle"
                                    label={translate("Specify other title:")}
                                    value={extraOtherTitle}
                                    required
                                    onChange={e =>
                                        textFieldChangeHandler(
                                            e,
                                            setExtraOtherTitle,
                                            setExtraOtherTitleError,
                                            translateOtherTitle,
                                            true,
                                            extraPersonsTitleIdCondition,
                                        )
                                    }
                                    error={isSubmitted && extraOtherTitleError}
                                />
                            </GridItem>
                        ) : (
                            <GridItem {...styles.halfWidthGridItem}></GridItem> /* Spacer */
                        )}
                    </>
                ) : (
                    <GridItem {...styles.halfWidthGridItem}></GridItem> /* Spacer */
                )}
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="operationSince"
                        label={translateOperationSince}
                        value={operationSince}
                        onChange={e => textFieldChangeHandler(e, setOperationSince)}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}></GridItem>
                {/* Spacer */}
                <GridItem {...styles.halfWidthGridItem}>
                    <Select
                        label={translateActivityField}
                        required
                        onChange={activityFieldChangeHandler}
                        error={isSubmitted && activityFieldError}
                    >
                        <option value="">{translateMakeChoice}</option>
                        <option value={translateTire}>{translateTire}</option>
                        <option value={translateReseller}>{translateReseller}</option>
                        <option value={translateDealer}>{translateDealer}</option>
                        <option value={translateOther}>{translateOther}</option>
                    </Select>
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    {activityFieldId !== 0 && activityFieldSpecifyBoxType !== "none" ? (
                        <TextField
                            name="activityFieldSpecifyBox"
                            label={translateActivityFieldSpecifyBox}
                            value={activityFieldSpecifyBox}
                            type={activityFieldSpecifyBoxType}
                            hint={activityFieldSpecifyBoxHint}
                            required={activityFieldId !== 2}
                            onChange={e => activityFieldBoxChangeHandler(e)}
                            error={isSubmitted && activityFieldSpecifyBoxError}
                        />
                    ) : (
                        <GridItem {...styles.halfWidthGridItem}></GridItem> /* Spacer */
                    )}
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <RadioGroup
                        name="poNumberRadio"
                        label={translate("Mandatory purchase order number")}
                        value={poNumberRadio}
                        required
                        onChangeHandler={e => setPoNumberRadio(e.target.value)}
                        {...styles.radioGroupStyles}
                    >
                        <Radio value="true" {...styles.radioButtonStyles}>
                            {translate("Yes")}
                        </Radio>
                        <Radio value="false" {...styles.radioButtonStyles}>
                            {translate("No")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        name="purchasesPlanned"
                        label={translate("Annual purchases planned")}
                        value={purchasesPlanned}
                        onChange={e => setPurchasesPlanned(e.target.value)}
                    />
                </GridItem>

                <GridItem {...styles.fullWidthGridItem}>
                    <TextArea
                        label={translate("Comments")}
                        value={comments}
                        onChange={e => commentsChangeHandler(e.currentTarget.value)}
                    />
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.sectionGridContainer}>
                <GridItem {...styles.fullWidthGridItem}>
                    <CheckboxGroup
                        {...styles.checkboxGroupStyles}
                        label={translate("Legal agreement")}
                        required
                        error={isSubmitted && legalAgreementError}
                    >
                        <Checkbox
                            onChange={legalAgreementChangeHandler}
                            checked={legalAgreement}
                            {...styles.checkboxStyles}
                        >
                            {translate("I agree that the terms of payment are net on the 10th of the following month.")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.sectionGridContainer}>
                <GridItem {...styles.fullWidthGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                </GridItem>
                {errorMessage && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                    </GridItem>
                )}
                {successMessage && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <Typography {...styles.submitSuccessTitle}>{successMessage}</Typography>
                    </GridItem>
                )}
                {isSubmitted && !isSubmitEnabled && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <Typography {...styles.submitErrorTitle}>
                            {translate("Please fill all required fields.")}
                        </Typography>
                    </GridItem>
                )}
                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                        data-test-selector="createAccount_createButton"
                    >
                        {translate("Create Account")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(RsswCreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
